import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
    id: string;
    email: string;
    name?: string;
    avatar?: string;
    language?: string;
    isSuperAdmin?: boolean;
    accounts: Array<{
        accountId: string;
        role: string;
        account: {
            name: string;
        };
    }>;
}

interface AuthContextType {
    user: User | null;
    accountId: string | null;
    loading: boolean;
    login: () => void;
    logout: () => void;
    selectAccount: (accountId: string) => Promise<void>;
    refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [accountId, setAccountId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchMe = async () => {
        try {
            const res = await fetch('/api/me');
            if (res.ok) {
                const data = await res.json();
                setUser(data.user);
                setAccountId(data.accountId);
            } else {
                setUser(null);
                setAccountId(null);
            }
        } catch (error) {
            console.error('Failed to fetch user', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMe();
    }, []);

    const login = () => {
        window.location.href = '/auth/google';
    };

    // NOTE: there is deliberately no browser-side path to /auth/test-login. The removed one shipped a
    // hardcoded X-E2E-Secret in the client bundle, i.e. the secret was public on every deploy where
    // the route existed. E2E tests hit that route directly with cy.request and supply the secret from
    // the environment; nothing the browser downloads should know it.

    const logout = () => {
        window.location.href = '/logout';
    };

    const selectAccount = async (id: string) => {
        const res = await fetch('/api/select-account', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accountId: id })
        });
        if (res.ok) {
            setAccountId(id);
        }
    };

    return (
        <AuthContext.Provider value={{ user, accountId, loading, login, logout, selectAccount, refresh: fetchMe }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};
